import { useId } from "@reach/auto-id";
import type * as Stitches from "@stitches/react";
import {
  config,
  css,
  styled,
  useThemeOverrides,
} from "@truepill/capsule-utils";
import type { ReactElement } from "react";
import * as React from "react";
import { Eye, EyeOff } from "react-feather";

import { ScreenReaderOnly } from "../../components/screen-reader-only/ScreenReaderOnly";
import { ErrorCircle } from "../../tools/icons";
import type { InputType } from "../../types";
import { Spacer } from "../spacer/Spacer";
import { Text } from "../text/Text";

type InputState = "default" | "complete" | "error";
type Size = "sm" | "lg";

interface BaseProps {
  state?: InputState;
  subCopy?: string;
  helperText?: string;
  size?: Size;
  label?: string;
  css?: Stitches.CSS<typeof config>;
  /**
   * Adds an icon to the start of the input.
   */
  startAdornment?: React.ElementType;
  /**
   * Adds an icon to the end of the input.
   */
  endAdornment?: React.ElementType;
}

export interface TextFieldProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">,
    BaseProps {
  type?: Exclude<InputType, "password">;
}

export interface PasswordFieldProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">,
    BaseProps {
  type: "password";
  showToggle?: boolean;
}

export interface TextFieldTextAreaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    BaseProps {
  type: "textarea";
}

const IconWrapper = styled("div", {
  position: "absolute",
  display: "flex",
  alignItems: "center",
  top: 0,
  right: 0,
  height: "100%",
  marginRight: "0.75rem",
});

const StyledAlert = styled("svg", {
  width: "24px",
  height: "24px",
  minWidth: "24px",
  color: "$functional-error-dark",
});

const AlertWrapper = styled("div", {
  display: "flex",
  alignItems: "flex-start",
});

const StyledCheck = styled("svg", {
  display: "none",
  width: "24px",
  fill: "$functional-success-dark",
  marginLeft: "0.25rem",
});

const baseTextFieldStyles = css({
  color: "$black",
  borderWidth: "0",
  boxShadow: "inset 0px 0px 0px 1px $colors$gray-700",
  background: "$white",
  padding: "0 1.75rem 0 0.75rem",
  borderRadius: "$sm",
  width: "100%",
  outline: "none",
  fontFamily: "$base",

  "&[disabled]": {
    pointerEvents: "none",
    cursor: "not-allowed",
  },

  "&:focus": {
    borderColor: "transparent",
    boxShadow: "inset 0px 0px 0px 3px $colors$primary-500",
  },

  // Disable the Edge implementation of password revealing, as it conflicts with ours
  "&::-ms-reveal": {
    display: "none",
  },

  "&::placeholder": {
    fontFamily: "$base",
  },

  variants: {
    size: {
      sm: {
        height: "48px",
      },
      lg: {
        height: "56px",
      },
    },
    hasIcon: {
      true: {
        padding: "0 2.5rem 0 0.75rem",
      },
    },
    hasStartAdornment: {
      true: {
        paddingLeft: "2.5rem",
      },
    },
    hasEndAdornment: {
      true: {
        paddingRight: "2.5rem",
      },
    },
    state: {
      default: {},
      error: {
        boxShadow: "inset 0px 0px 0px $space$4xs $colors$functional-error-dark",
        background: "$colors$white",
        "&:focus": {
          boxShadow: "inset 0px 0px 0px 3px $colors$functional-error-dark",
        },
      },
      complete: {
        padding: "0 2.5rem 0 0.75rem",
        background: "$gray-100",
        borderColor: "$gray-500",
        "&:focus": {
          border: "$primary-500",
        },
      },
    },
  },
  defaultVariants: {
    state: "default",
    size: "lg",
  },
  compoundVariants: [
    {
      state: "complete",
      hasIcon: true,
      css: {
        padding: "0 4.25rem 0 0.75rem",
      },
    },
  ],
});

const StyledTextField = styled("input", baseTextFieldStyles);

const Wrapper = styled("div", {
  position: "relative",

  "&[data-state='complete']": {
    [`${StyledCheck}`]: {
      display: "block",
    },
  },
  textarea: {
    paddingTop: "1rem",
    paddingBottom: "1rem",

    height: "auto",
  },

  "&:focus-within": {
    color: "$primary-500",
  },

  "&[data-state='error']": {
    color: "$functional-error-dark",
  },

  variants: {
    hasStartAdornment: {
      false: {
        "&:focus-within": {
          color: "currentColor",
        },
      },
    },
    hasEndAdornment: {
      false: {
        "&:focus-within": {
          color: "currentColor",
        },
      },
    },
  },
});

const HelperTextWrapper = styled("div", {
  display: "inline-flex",
  marginTop: "$xs",
});

const StyledHelperText = styled(Text, {
  display: "block",
  fontFamily: "$base",
  alignSelf: "center",
  "&.error": {
    color: "$functional-error-dark",
    marginLeft: "0.125rem",
    fontWeight: 700,
  },
});

const StyledSubCopy = styled(Text, {
  flexBasis: "100%",
  marginTop: "$xs",
  color: "$typography-medium",
  fontWeight: "normal",
});

const LabelRequiredWrapper = styled("span", {
  display: "flex",
  alignItems: "center",
  ".required": {
    color: "$functional-error-dark",
    marginLeft: "0.125rem",
  },
  ".field-title": {
    whiteSpace: "normal",
  },
});

const StyledWrapper = styled("div", {
  display: "inline-block",
  width: "100%",
  variants: {
    disabled: {
      true: {
        opacity: 0.5,
      },
    },
  },
});

const AdornmentWrapper = styled("div", {
  position: "absolute",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",

  variants: {
    startAdornment: {
      true: {
        left: "0.5rem",
      },
    },
    endAdornment: {
      true: {
        right: "0.5rem",
        top: 0,
      },
    },
  },
});

const ShowPasswordButton = styled("button", {
  all: "unset",
  border: "1px solid transparent",
  "&:focus-visible": {
    border: "1px solid $primary-500",
  },
  "> svg": {
    color: "$colors$gray-700",
  },
});

/**
 * Allows users to enter and edit text. Also able to render a `<textfield />` by passing `type="textField"`
 */
export const TextField = React.forwardRef<
  any,
  TextFieldProps | TextFieldTextAreaProps | PasswordFieldProps
>((props, ref): ReactElement => {
  const {
    state = "default",
    size,
    label,
    required,
    subCopy,
    helperText,
    type = "text",
    css,
    disabled,
    startAdornment: StartAdornment,
    endAdornment: EndAdornment,
    ...rest
  } = props;
  const id = useId(rest.id);
  const uniqueId = useId(id);
  const overrides = useThemeOverrides("textField");
  const [displayPassword, setDisplayPassword] = React.useState(false);

  const checkInputType = () => {
    if (type === "textarea") {
      return undefined;
    }

    if (type === "password" && displayPassword) {
      return "text";
    }

    return type;
  };

  const isPasswordType = (
    type: string | undefined,
    x: unknown
  ): x is PasswordFieldProps => {
    return type === "password";
  };

  /**
   * This is used to overcome issues in TypeScript with losing
   * type narrowing when discriminated unions are destructured
   */
  const getInputProps = () => {
    if (isPasswordType(props.type, rest)) {
      const { showToggle, ...additional } = rest;
      return { showToggle, props: additional };
    }

    return {
      showToggle: false,
      props: rest,
    };
  };

  const { showToggle, props: styledTextFieldProps } = getInputProps();

  return (
    <StyledWrapper disabled={disabled}>
      {label && (
        <>
          <Text as="label" htmlFor={id} variant="body" bold>
            <LabelRequiredWrapper>
              <span className="field-title">{label}</span>
            </LabelRequiredWrapper>
            {subCopy && <StyledSubCopy variant="body">{subCopy}</StyledSubCopy>}
          </Text>
          <Spacer size="xs" />
        </>
      )}
      <Wrapper
        data-state={state}
        hasStartAdornment={!!StartAdornment}
        hasEndAdornment={!!EndAdornment}
      >
        {!!StartAdornment && (
          <AdornmentWrapper startAdornment>
            <StartAdornment data-testid="startAdornment" />
          </AdornmentWrapper>
        )}
        <StyledTextField
          size={size}
          required={required}
          aria-invalid={state === "error"}
          aria-describedby={
            helperText && state !== "error" ? uniqueId : undefined
          }
          aria-errormessage={
            helperText && state === "error" ? uniqueId : undefined
          }
          state={state}
          hasIcon={showToggle}
          hasStartAdornment={!!StartAdornment}
          hasEndAdornment={!!EndAdornment}
          id={id}
          as={type === "textarea" ? "textarea" : "input"}
          ref={ref}
          css={{ ...(overrides && overrides), ...css }}
          type={checkInputType()}
          disabled={disabled}
          {...styledTextFieldProps}
        />
        <IconWrapper>
          {showToggle && (
            <ShowPasswordButton
              aria-hidden
              onClick={() => setDisplayPassword(!displayPassword)}
              disabled={disabled}
              type="button"
            >
              <ScreenReaderOnly>
                {displayPassword ? "Hide" : "Show"} Password Display
              </ScreenReaderOnly>
              {displayPassword ? (
                <Eye aria-hidden="true" />
              ) : (
                <EyeOff aria-hidden="true" />
              )}
            </ShowPasswordButton>
          )}
          <StyledCheck
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            aria-hidden="true"
            data-testid="checkIcon"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </StyledCheck>
        </IconWrapper>
        {!!EndAdornment && (
          <AdornmentWrapper endAdornment>
            <EndAdornment data-testid="endAdornment" />
          </AdornmentWrapper>
        )}
      </Wrapper>
      {helperText && (
        <HelperTextWrapper>
          {state === "error" && (
            <AlertWrapper>
              <StyledAlert
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
                data-testid="alertIcon"
              >
                <ErrorCircle />
              </StyledAlert>
            </AlertWrapper>
          )}
          <StyledHelperText className={state} variant="body-sm" id={uniqueId}>
            {helperText}
          </StyledHelperText>
        </HelperTextWrapper>
      )}
    </StyledWrapper>
  );
});
